import { useCallback, useEffect, useRef, useState } from 'react';

import { type FieldClaim } from './claims';

/**
 * How long an override waits for its focus to land before expiring. The
 * deferred focus arrives within a frame or two; the TTL only has to beat a
 * focus that never lands at all (unmounted ref, native focus refusal).
 */
export const UNUSED_OVERRIDE_TTL_MS = 2000;

export interface ClaimGate {
  /** True when a claim on the field should stop the local user editing it. */
  isBlocked(fieldKey: string): boolean;
  fieldFocused(fieldKey: string): void;
  fieldBlurred(fieldKey: string): void;
  /** Explicit per-focus-session opt-in to edit despite the claim. */
  override(fieldKey: string): void;
}

/**
 * Decides whether a focus claim blocks the local user (CONTEXT.md: Focus
 * claim, Override, Co-editing). A claim never blocks the field the user is
 * already in (never eject — a race becomes co-editing), and an override
 * unblocks a field for exactly one focus session: it dies with that field's
 * blur, when the user focuses a different field before using it, or — if no
 * focus ever consumes it — after a short expiry, so it can't linger and
 * silently unblock a later, unrelated focus session ("never sticky").
 */
export function useClaimGate(claims: Record<string, FieldClaim[]>): ClaimGate {
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [overriddenField, setOverriddenField] = useState<string | null>(null);

  const overrideExpiry = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearOverrideExpiry = useCallback(() => {
    if (overrideExpiry.current) {
      clearTimeout(overrideExpiry.current);
      overrideExpiry.current = null;
    }
  }, []);
  useEffect(() => clearOverrideExpiry, [clearOverrideExpiry]);

  const fieldFocused = useCallback(
    (fieldKey: string) => {
      // Any focus settles the pending override: consumed if it's this field,
      // dropped otherwise — either way the expiry has nothing left to do.
      clearOverrideExpiry();
      setFocusedField(fieldKey);
      setOverriddenField((cur) => (cur === fieldKey ? cur : null));
    },
    [clearOverrideExpiry],
  );

  const fieldBlurred = useCallback((fieldKey: string) => {
    setFocusedField((cur) => (cur === fieldKey ? null : cur));
    setOverriddenField((cur) => (cur === fieldKey ? null : cur));
  }, []);

  const override = useCallback(
    (fieldKey: string) => {
      setOverriddenField(fieldKey);
      clearOverrideExpiry();
      overrideExpiry.current = setTimeout(() => {
        overrideExpiry.current = null;
        setOverriddenField(null);
      }, UNUSED_OVERRIDE_TTL_MS);
    },
    [clearOverrideExpiry],
  );

  const isBlocked = useCallback(
    (fieldKey: string) =>
      fieldKey in claims && focusedField !== fieldKey && overriddenField !== fieldKey,
    [claims, focusedField, overriddenField],
  );

  return { isBlocked, fieldFocused, fieldBlurred, override };
}
